import React, { useState, useCallback, useMemo } from 'react';
import { Typography } from '../../common';
import MemoizedCallItem from './MemoizedCallItem';
import { LiveCall } from 'redux/slices/liveCalls.slice';
import InfoDrawer from './InfoDrawer';
import { useInfoStatus } from 'hooks/useInfoStatus';
import { CrashSafeWrapper } from '../../CrashSafeWrapper';

interface CallListProps {
    calls: LiveCall[];
    onHangUp: (callUUID: string) => void;
    onRecord: (callUUID: string, action: "start" | "stop") => void;
    onClickCall: (call: LiveCall) => void;
    onTransfer?: (callUUID: string, selectedCallExt: string, destination: string) => void;
    onDndTransfer?: (callUUID: string, targetExt: string, callees: any[], callerIDNumber: string, callerIDName: string, destination: string) => void;
}

const CallList: React.FC<CallListProps> = ({
    calls,
    onHangUp,
    onRecord,
    onClickCall,
    onTransfer,
    onDndTransfer,
}) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedCall, setSelectedCall] = useState<LiveCall | undefined>(undefined);
    const { getInfoText } = useInfoStatus();

    const handleClick = useCallback((call: LiveCall) => {
        setSelectedCall(call);
        setIsDrawerOpen(true);
        onClickCall(call);
    }, [onClickCall]);

    const handleCloseDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setSelectedCall(undefined);
    }, []);

    const handleRecord = useCallback((callUUID: string, action: "start" | "stop") => {
        onRecord(callUUID, action);
    }, [onRecord]);

    const handleHangUp = useCallback((callUUID: string) => {
        onHangUp(callUUID);
    }, [onHangUp]);

    if (calls.length === 0) {
        return (
            <div className="no-calls-text">
                <Typography variant="text-1" className="text-center">
                    No Active Calls found
                </Typography>
            </div>
        );
    }

    return (
        <CrashSafeWrapper level="component" name="CallList">
            <ul className="active-calls-list-container">
                {calls.map(call => (
                    <MemoizedCallItem
                        key={call.callUUID}
                        call={call}
                        onRecord={(callUUID) => handleRecord(callUUID, call.isRecording ? "stop" : "start")}
                        onHangUp={handleHangUp}
                        onClickCall={handleClick}
                        onTransfer={onTransfer}
                        onDndTransfer={onDndTransfer}
                    />
                ))}
            </ul>
        </CrashSafeWrapper>
    );
};

export default React.memo(CallList);
